import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CheckCircle2, Loader2, RotateCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { HelpHint } from '../components/HelpHint';
import { api } from '../lib/api';

/**
 * Failed deliveries, and the two things that can be done about one.
 *
 * A dead-lettered message means a technician was not told about a job, or a
 * landlord has no token. That has to be somewhere a person looks, not
 * somewhere a log rotates away.
 *
 * ## Why there are two buttons
 *
 * Replay puts the same message back on the queue, which is right when the
 * ladder gave up — a relay that was down, a network that timed out. It is
 * useless when the network *refused* the message: the same text to the same
 * wrong number fails the same way, and that case never reaches the ladder at
 * all, so it carries no `deadLetteredAt` and replay will not touch it.
 *
 * Those rows need the fix to happen elsewhere — a corrected number and a fresh
 * send — and then somewhere to record that it happened. Without that they
 * stayed on this page for ever and kept their alert up with nothing that could
 * clear it.
 */
export function DeadLettersPage() {
  const queryClient = useQueryClient();

  const letters = useQuery({
    queryKey: ['dead-letters'],
    queryFn: api.deadLetters,
    refetchInterval: 30_000,
  });

  const replay = useMutation({
    mutationFn: (id: string) => api.replayDeadLetter(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dead-letters'] }),
  });

  const resolve = useMutation({
    mutationFn: (id: string) => api.resolveDeadLetter(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dead-letters'] }),
  });

  if (letters.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  const messages = letters.data?.messages ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <div>
        <h2 className="flex items-center gap-1.5 font-heading text-2xl">
          Failed deliveries
          <HelpHint>
            Messages that exhausted every retry, and messages the network refused outright. Fix
            the cause — usually a wrong number — then replay. Replaying resets the attempts but
            keeps the history. A message the network refused cannot be replayed: send a fresh one
            to the corrected number, then mark this one dealt with so it stops being counted.
          </HelpHint>
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {messages.length === 0 ? 'Nothing has failed.' : `${messages.length} need attention`}
        </p>
      </div>

      {messages.length === 0 ? (
        <div className="card py-12 text-center">
          <CheckCircle2 className="mx-auto h-8 w-8 text-secondary-500" aria-hidden="true" />
          <p className="mt-3 text-muted-foreground">
            Every message has been delivered or is still retrying.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {messages.map((message) => (
            <li key={message.id} className="card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="badge bg-danger-50 text-danger-600">{message.channel}</span>
                    <span className="font-medium">{message.toAddress}</span>
                    {message.job && (
                      <span className="text-sm text-muted-foreground">
                        {message.job.reference} — {message.job.title}
                      </span>
                    )}
                  </div>

                  <p className="mt-1.5 text-sm text-danger-600">
                    {message.errorCode}: {message.errorDetail}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {message.kind} ·{' '}
                    {message.deadLetteredAt
                      ? `gave up after ${message.attempts} attempt${message.attempts === 1 ? '' : 's'} · ${new Date(message.deadLetteredAt).toLocaleString('en-KE')}`
                      : 'the network refused it — replaying cannot help'}
                  </p>
                </div>

                <div className="flex shrink-0 gap-2">
                  {/*
                    * Offered only where it can work. Replay matches on
                    * `deadLetteredAt`, so on a refused message it would return
                    * "not found" — a button that always fails is worse than no
                    * button, because the failure reads as the system being
                    * broken rather than as the wrong tool.
                    */}
                  {message.deadLetteredAt ? (
                    <button
                      type="button"
                      onClick={() => replay.mutate(message.id)}
                      disabled={replay.isPending}
                      className="btn-secondary"
                    >
                      {replay.isPending && replay.variables === message.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <RotateCw className="h-4 w-4" aria-hidden="true" />
                      )}
                      Replay
                    </button>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => resolve.mutate(message.id)}
                    disabled={resolve.isPending}
                    className="btn-secondary"
                    title="Stop counting this one — the problem was handled another way"
                  >
                    {resolve.isPending && resolve.variables === message.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Check className="h-4 w-4" aria-hidden="true" />
                    )}
                    Dealt with
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="text-sm text-muted-foreground">
        Not seeing a message you expected? It may still be retrying — check the{' '}
        <Link to="/" className="text-primary-600 underline">
          ticket timeline
        </Link>
        .
      </p>
    </div>
  );
}
